import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "react-i18next";
import { BarChart3, ArrowLeft, Loader2, TrendingUp, TrendingDown, Target, Crosshair, Shield, Swords } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/features/auth/auth-store";
import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";

interface StatsData {
  totals: {
    handsPlayed: number;
    handsWon: number;
    netChips: number;
    biggestPot: number;
  };
  rates: {
    vpip: number;
    pfr: number;
    wtsd: number;
    wsd: number;
    wwsf: number;
    ats: number;
    threeBet: number;
  };
  byPosition: Record<string, { hands: number; vpip: number; pfr: number; won: number }>;
  aggression: Record<string, number>;
  foldFrequency: Record<string, number>;
  recentHands: Array<{ handId: string; result: number; position: string; date: string | null }>;
}

async function fetchStats(token: string): Promise<StatsData> {
  return apiFetch<StatsData>("/api/stats", { token });
}

const POSITION_ORDER = ["UTG", "UTG+1", "MP", "MP+1", "HJ", "CO", "BTN", "SB", "BB"];

const StatCard = ({ label, value, color = "text-foreground", sub }: { label: string; value: string; color?: string; sub?: string }) => (
  <Card className="p-4 text-center">
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </Card>
);

const RateBar = ({ label, value, tooltip, optimal }: { label: string; value: number; tooltip: string; optimal?: string }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium">{label}</span>
      <span className="text-sm font-bold">{value}%</span>
    </div>
    <Progress value={Math.min(value, 100)} className="h-2" />
    <div className="flex justify-between">
      <span className="text-xs text-muted-foreground">{tooltip}</span>
      {optimal && <span className="text-xs text-accent">{optimal}</span>}
    </div>
  </div>
);

const Stats = () => {
  const { t } = useTranslation();
  const authToken = useAuthStore((s) => s.token);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!authToken) return;
    fetchStats(authToken)
      .then(setStats)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [authToken]);

  const winRate = stats && stats.totals.handsPlayed > 0
    ? ((stats.totals.handsWon / stats.totals.handsPlayed) * 100).toFixed(1)
    : "0.0";

  const netColor = stats && stats.totals.netChips >= 0 ? "text-emerald-400" : "text-red-400";
  const netPrefix = stats && stats.totals.netChips >= 0 ? "+" : "";

  const sortedPositions = stats
    ? POSITION_ORDER.filter((p) => p in stats.byPosition)
    : [];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-5 max-w-screen-xl mx-auto">
        {/* Header */}
        <header className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon" className="shrink-0">
            <Link to="/profile">
              <ArrowLeft className="w-6 h-6" />
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-3 rounded-xl text-primary">
              <BarChart3 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t("stats_title")}</h1>
              <p className="text-sm text-muted-foreground">{t("stats_subtitle")}</p>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error || !stats ? (
          <p className="text-center text-muted-foreground py-12">
            {t("stats_error", "No se pudieron cargar las estadísticas")}
          </p>
        ) : (
          <>
            {/* Overview Cards */}
            <section>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  label="Manos Jugadas"
                  value={stats.totals.handsPlayed.toLocaleString()}
                />
                <StatCard
                  label="Manos Ganadas"
                  value={stats.totals.handsWon.toLocaleString()}
                  color="text-emerald-400"
                />
                <StatCard
                  label="Win Rate"
                  value={`${winRate}%`}
                  color="text-primary"
                />
                <StatCard
                  label="Resultado Neto"
                  value={`${netPrefix}${stats.totals.netChips.toLocaleString()}`}
                  color={netColor}
                />
                <StatCard
                  label="Bote Más Grande"
                  value={stats.totals.biggestPot.toLocaleString()}
                  color="text-secondary"
                />
              </div>
            </section>

            {/* Preflop Rates */}
            <section>
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Estadísticas Preflop
              </h2>
              <Card className="p-4 space-y-4">
                <RateBar
                  label="VPIP"
                  value={stats.rates.vpip}
                  tooltip="Voluntarily Put $ In Pot"
                  optimal="Óptimo: 20-28%"
                />
                <RateBar
                  label="PFR"
                  value={stats.rates.pfr}
                  tooltip="Pre-Flop Raise"
                  optimal="Óptimo: 17-23%"
                />
                <RateBar
                  label="ATS"
                  value={stats.rates.ats}
                  tooltip="Attempt To Steal"
                  optimal="Óptimo: 25-35%"
                />
                <RateBar
                  label="3-Bet"
                  value={stats.rates.threeBet}
                  tooltip="Re-raise preflop"
                  optimal="Óptimo: 6-10%"
                />
              </Card>
            </section>

            {/* Postflop Rates */}
            <section>
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                <Crosshair className="w-5 h-5 text-secondary" />
                Estadísticas Postflop
              </h2>
              <Card className="p-4 space-y-4">
                <RateBar
                  label="WTSD"
                  value={stats.rates.wtsd}
                  tooltip="Went To Showdown"
                  optimal="Óptimo: 25-32%"
                />
                <RateBar
                  label="W$SD"
                  value={stats.rates.wsd}
                  tooltip="Won $ at Showdown"
                  optimal="Óptimo: 50-55%"
                />
                <RateBar
                  label="WWSF"
                  value={stats.rates.wwsf}
                  tooltip="Won When Saw Flop"
                  optimal="Óptimo: 45-55%"
                />
              </Card>
            </section>

            {/* Aggression Factor */}
            <section>
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                <Swords className="w-5 h-5 text-accent" />
                Factor de Agresión
              </h2>
              <Card className="p-4">
                <div className="grid grid-cols-4 gap-2 text-center">
                  {(["preflop", "flop", "turn", "river"] as const).map((phase) => (
                    <div key={phase}>
                      <p className="text-xs text-muted-foreground capitalize mb-1">{phase}</p>
                      <p className="text-xl font-bold">
                        {stats.aggression[phase]?.toFixed(1) ?? "0.0"}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  AF = (bets + raises) / calls — Óptimo: 2.0-3.5
                </p>
              </Card>
            </section>

            {/* Fold Frequency */}
            <section>
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-muted-foreground" />
                Frecuencia de Fold
              </h2>
              <Card className="p-4">
                <div className="grid grid-cols-4 gap-2 text-center">
                  {(["preflop", "flop", "turn", "river"] as const).map((phase) => (
                    <div key={phase}>
                      <p className="text-xs text-muted-foreground capitalize mb-1">{phase}</p>
                      <p className="text-xl font-bold">
                        {stats.foldFrequency[phase]?.toFixed(1) ?? "0.0"}%
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  % de veces que foldeas ante agresión del rival
                </p>
              </Card>
            </section>

            {/* By Position */}
            {sortedPositions.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-3">Estadísticas por Posición</h2>
                <Card className="p-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-muted-foreground text-xs">
                        <th className="text-left pb-2">Pos</th>
                        <th className="text-right pb-2">Manos</th>
                        <th className="text-right pb-2">VPIP</th>
                        <th className="text-right pb-2">PFR</th>
                        <th className="text-right pb-2">Won</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPositions.map((pos) => {
                        const d = stats.byPosition[pos];
                        return (
                          <tr key={pos} className="border-t border-border/50">
                            <td className="py-1.5 font-medium">{pos}</td>
                            <td className="py-1.5 text-right">{d.hands}</td>
                            <td className="py-1.5 text-right">{d.vpip}%</td>
                            <td className="py-1.5 text-right">{d.pfr}%</td>
                            <td className="py-1.5 text-right text-emerald-400">{d.won}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Card>
              </section>
            )}

            {/* Recent Hands */}
            {stats.recentHands.length > 0 && (
              <section>
                <h2 className="text-lg font-bold mb-3">Últimas Manos</h2>
                <Card className="p-4 space-y-2">
                  {stats.recentHands.map((hand) => (
                    <div
                      key={hand.handId}
                      className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        {hand.result >= 0 ? (
                          <TrendingUp className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-400" />
                        )}
                        <span className="text-sm font-medium">{hand.position}</span>
                      </div>
                      <span
                        className={`text-sm font-bold ${hand.result >= 0 ? "text-emerald-400" : "text-red-400"}`}
                      >
                        {hand.result >= 0 ? "+" : ""}
                        {hand.result.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </Card>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Stats;
